const USERS_KEY = 'miralink_users';
const CURRENT_USER_KEY = 'user';

const readUsers = () => {
  try {
    return JSON.parse(localStorage.getItem(USERS_KEY) || '[]');
  } catch {
    return [];
  }
};

const writeUsers = (users) => {
  try {
    localStorage.setItem(USERS_KEY, JSON.stringify(users));
  } catch {
    throw new Error('Браузер не дал сохранить аккаунт. Проверьте, что включены cookies/localStorage и не открыт приватный режим.');
  }
};

const publicUser = (user) => ({
  id: user.id,
  username: user.username,
  nickname: user.nickname || null,
  avatar: user.avatar || null,
  bio: user.bio || null,
  created_at: user.created_at
});

const privateUser = (user) => ({
  ...publicUser(user),
  phone: null,
  email: user.email,
  phone_verified: false,
  email_verified: true
});

const makeToken = (userId) => `local-${userId}-${Date.now()}`;

const makeId = () => {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getCurrentStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem(CURRENT_USER_KEY) || 'null');
  } catch {
    return null;
  }
};

export const authAPI = {
  register: async (email, username, password) => {
    const normalizedEmail = email.trim().toLowerCase();
    const normalizedUsername = username.trim().toLowerCase();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      throw new Error('Введите корректный email');
    }

    if (!/^[a-zA-Z0-9_]{3,30}$/.test(normalizedUsername)) {
      throw new Error('Username должен быть от 3 до 30 символов: латиница, цифры и нижнее подчеркивание');
    }

    if (password.length < 6) {
      throw new Error('Пароль должен быть не короче 6 символов');
    }

    const users = readUsers();
    if (users.some((user) => user.email === normalizedEmail)) {
      throw new Error('Email уже зарегистрирован');
    }
    if (users.some((user) => user.username === normalizedUsername)) {
      throw new Error('Username уже занят');
    }

    const user = {
      id: makeId(),
      email: normalizedEmail,
      username: normalizedUsername,
      password,
      nickname: null,
      avatar: null,
      bio: null,
      created_at: new Date().toISOString()
    };

    writeUsers([...users, user]);

    return {
      token: makeToken(user.id),
      user: privateUser(user)
    };
  },

  login: async (email, password) => {
    const normalizedEmail = email.trim().toLowerCase();
    const user = readUsers().find((item) => item.email === normalizedEmail && item.password === password);

    if (!user) {
      throw new Error('Неверный email или пароль');
    }

    return {
      token: makeToken(user.id),
      user: privateUser(user)
    };
  },

  getMe: async () => {
    const storedUser = getCurrentStoredUser();
    if (!storedUser?.id) {
      throw new Error('Пользователь не найден');
    }

    const user = readUsers().find((item) => item.id === storedUser.id) || storedUser;
    return privateUser(user);
  },

  getUserProfile: async (username) => {
    const normalizedUsername = username.trim().toLowerCase().replace(/^@/, '');
    const user = readUsers().find((item) => item.username === normalizedUsername);
    if (!user) {
      throw new Error('Пользователь не найден');
    }

    return publicUser(user);
  }
};

export const profileAPI = {
  updateProfile: async (token, username, nickname, avatar, bio) => {
    const currentUser = getCurrentStoredUser();
    if (!currentUser?.id) {
      throw new Error('Пользователь не найден');
    }

    const users = readUsers();
    const normalizedUsername = username ? username.trim().toLowerCase() : currentUser.username;

    if (!/^[a-zA-Z0-9_]{3,30}$/.test(normalizedUsername)) {
      throw new Error('Username должен быть от 3 до 30 символов');
    }

    if (users.some((user) => user.id !== currentUser.id && user.username === normalizedUsername)) {
      throw new Error('Username уже занят');
    }

    let updatedUser = null;
    const nextUsers = users.map((user) => {
      if (user.id !== currentUser.id) return user;

      updatedUser = {
        ...user,
        username: normalizedUsername,
        nickname: nickname || null,
        avatar: avatar || null,
        bio: bio || null
      };
      return updatedUser;
    });

    if (!updatedUser) {
      updatedUser = {
        ...currentUser,
        username: normalizedUsername,
        nickname: nickname || null,
        avatar: avatar || null,
        bio: bio || null
      };
      nextUsers.push(updatedUser);
    }

    writeUsers(nextUsers);
    return privateUser(updatedUser);
  },

  searchUsers: async (token, query) => {
    const currentUser = getCurrentStoredUser();
    const normalizedQuery = query.trim().toLowerCase();
    return readUsers()
      .filter((user) => user.id !== currentUser?.id)
      .filter((user) => user.username.includes(normalizedQuery) || (user.nickname || '').toLowerCase().includes(normalizedQuery))
      .map(publicUser);
  },

  getAllUsers: async () => {
    const currentUser = getCurrentStoredUser();
    return readUsers()
      .filter((user) => user.id !== currentUser?.id)
      .map(publicUser);
  }
};

export const galleryAPI = {
  addMedia: async () => [],
  getMyMedia: async () => []
};
